import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, MessageCircle, FileText, ChevronLeft, ChevronRight } from "lucide-react";

const PAGE_SIZE = 20;
import { toast } from "sonner";

export default function ReceiptsTab({ receipts, owner }) {
  const backend = process.env.REACT_APP_BACKEND_URL;
  const [page, setPage] = useState(0);
  const totalPages = Math.max(1, Math.ceil(receipts.length / PAGE_SIZE));
  useEffect(() => { if (page > totalPages - 1) setPage(totalPages - 1); }, [page, totalPages]);
  const visible = receipts.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const download = (r) => window.open(`${backend}${r.pdf_url}`, "_blank");
  const share = (r) => {
    const link = `${backend}${r.pdf_url}`;
    const msg = `Hi ${r.tenant_name},\n\nHere is your rent receipt from ${owner?.property_name} for ${r.month_year}.\n\n• Receipt #: ${r.receipt_no}\n• Amount Paid: ₹ ${Number(r.amount_paid).toLocaleString("en-IN")}\n• Mode: ${r.payment_mode}\n• Date: ${r.payment_date}\n\nDownload PDF: ${link}\n\nThank you!\n— ${owner?.property_name}`;
    const num = (r.tenant_whatsapp || "").replace(/[^0-9]/g, "");
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank");
    toast.success("WhatsApp opened");
  };

  return (
    <Card className="p-6 border-slate-200">
      <div className="mb-5">
        <h2 className="font-display text-xl font-semibold flex items-center gap-2"><FileText className="h-5 w-5 text-indigo-600" /> Downloads</h2>
        <p className="text-sm text-slate-500 mt-0.5">All generated receipt PDFs — download or re-share anytime.</p>
      </div>

      {receipts.length === 0 ? (
        <div className="py-12 text-center text-slate-500">
          No receipts yet. Head to <span className="font-semibold text-slate-700">Generate Receipt</span> to create your first one.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <Th>Receipt #</Th><Th>Tenant</Th><Th>Room</Th><Th>Month</Th><Th>Amount</Th><Th>Mode</Th><Th>Date</Th><Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid="receipts-table-row">
                  <Td className="font-mono text-xs text-indigo-700">{r.receipt_no}</Td>
                  <Td className="font-medium">{r.tenant_name}</Td>
                  <Td className="font-mono text-xs">{r.room_no}</Td>
                  <Td>{r.month_year}</Td>
                  <Td className="font-semibold text-emerald-700">₹ {Number(r.amount_paid).toLocaleString("en-IN")}</Td>
                  <Td>{r.payment_mode}</Td>
                  <Td className="text-xs">{r.payment_date}</Td>
                  <Td className="text-right">
                    <a href={`${backend}${r.pdf_url}`} target="_blank" rel="noopener noreferrer" data-testid="receipts-table-download-btn">
                      <Button size="sm" variant="ghost" onClick={(e) => { e.preventDefault(); download(r); }}><Download className="h-4 w-4" /></Button>
                    </a>
                    <Button size="sm" variant="ghost" onClick={() => share(r)} className="text-[#25D366] hover:bg-emerald-50" data-testid="receipts-table-whatsapp-btn"><MessageCircle className="h-4 w-4" /></Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-4" data-testid="receipts-pagination">
              <span className="text-xs text-slate-500" data-testid="receipts-pagination-info">
                Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, receipts.length)} of {receipts.length}
              </span>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid="receipts-prev-btn"><ChevronLeft className="h-4 w-4" /> Previous</Button>
                <span className="text-xs font-medium text-slate-600" data-testid="receipts-page-indicator">Page {page + 1} of {totalPages}</span>
                <Button size="sm" variant="outline" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)} data-testid="receipts-next-btn">Next <ChevronRight className="h-4 w-4" /></Button>
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

const Th = ({ children, className = "" }) => <th className={`py-3 pr-4 font-semibold ${className}`}>{children}</th>;
const Td = ({ children, className = "" }) => <td className={`py-3 pr-4 ${className}`}>{children}</td>;
